/* Starlyzed browser-local light/dark control. No account, network, or server state.
   Appearance toggle is founder-locked off: stored preferences stay readable but the
   current storefront always resolves/applies light until APPEARANCE_TOGGLE_ENABLED. */
(function stzAppearanceController(windowRef) {
  'use strict';

  const STORAGE_KEY = 'stz:appearance:v1';
  const ROOT_ATTRIBUTE = 'data-stz-appearance';
  const DEFAULT_APPEARANCE = 'light';
  const APPEARANCE_TOGGLE_ENABLED = false;
  const CONTROLLER_MARKER = '__stzAppearanceController';
  const TOGGLE_SELECTOR = '[data-stz-appearance-toggle]';

  function isAppearance(value) {
    return value === 'light' || value === 'dark';
  }

  function storageFor(storage) {
    if (storage !== undefined) return storage;
    try {
      return windowRef.localStorage;
    } catch (error) {
      return null;
    }
  }

  function readStoredAppearance(storage) {
    const source = storageFor(storage);
    if (!source) return DEFAULT_APPEARANCE;
    try {
      const value = source.getItem(STORAGE_KEY);
      return isAppearance(value) ? value : DEFAULT_APPEARANCE;
    } catch (error) {
      return DEFAULT_APPEARANCE;
    }
  }

  function writeStoredAppearance(storage, value) {
    const source = storageFor(storage);
    if (!source || !isAppearance(value)) return false;
    try {
      source.setItem(STORAGE_KEY, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function resolveInitialAppearance(storage) {
    if (!APPEARANCE_TOGGLE_ENABLED) return DEFAULT_APPEARANCE;
    return readStoredAppearance(storage);
  }

  function applyAppearance(root, value) {
    const appearance = isAppearance(value) ? value : DEFAULT_APPEARANCE;
    if (root && typeof root.setAttribute === 'function') {
      root.setAttribute(ROOT_ATTRIBUTE, appearance);
    }
    return appearance;
  }

  function controlState(appearance) {
    const dark = appearance === 'dark';
    const target = dark ? 'Light' : 'Dark';
    return {
      pressed: dark,
      label: `Switch to ${target} mode`,
      visibleLabel: `${target} mode`,
    };
  }

  function updateControls(documentRef, appearance) {
    const state = controlState(appearance);
    const buttons = documentRef.querySelectorAll(TOGGLE_SELECTOR);
    for (const button of buttons) {
      const pressed = String(state.pressed);
      if (button.getAttribute('aria-pressed') !== pressed) {
        button.setAttribute('aria-pressed', pressed);
      }
      if (button.getAttribute('aria-label') !== state.label) {
        button.setAttribute('aria-label', state.label);
      }
      if (button.getAttribute('title') !== state.label) {
        button.setAttribute('title', state.label);
      }
      const label = button.querySelector('[data-stz-appearance-label]');
      if (label) {
        if (label.textContent !== state.visibleLabel) {
          label.textContent = state.visibleLabel;
        }
      } else if (button.textContent !== state.visibleLabel) {
        button.textContent = state.visibleLabel;
      }
    }
  }

  function bindAppearanceController({ documentRef = windowRef.document, root = documentRef?.documentElement, storage } = {}) {
    if (!documentRef || !root) return null;

    const existing = documentRef[CONTROLLER_MARKER];
    if (existing) {
      existing.sync();
      return existing;
    }

    const storageRef = storageFor(storage);
    let current = applyAppearance(root, resolveInitialAppearance(storageRef));
    const sync = () => updateControls(documentRef, current);
    const onClick = (event) => {
      if (!APPEARANCE_TOGGLE_ENABLED) return;
      const button = event?.target?.closest?.(TOGGLE_SELECTOR);
      const contains = typeof documentRef.contains !== 'function' || documentRef.contains(button);
      if (!button || !contains) return;

      const next = current === 'dark' ? 'light' : 'dark';
      current = applyAppearance(root, writeStoredAppearance(storageRef, next) ? next : DEFAULT_APPEARANCE);
      sync();
    };

    documentRef.addEventListener('click', onClick);

    let observer = null;
    if (typeof windowRef.MutationObserver === 'function') {
      observer = new windowRef.MutationObserver(sync);
      observer.observe(documentRef.documentElement || documentRef, { childList: true, subtree: true });
    }

    const controller = {
      sync,
      get appearance() {
        return current;
      },
      destroy() {
        documentRef.removeEventListener('click', onClick);
        observer?.disconnect();
        if (documentRef[CONTROLLER_MARKER] === controller) delete documentRef[CONTROLLER_MARKER];
      },
    };
    documentRef[CONTROLLER_MARKER] = controller;
    sync();
    return controller;
  }

  windowRef.StzAppearance = {
    STORAGE_KEY,
    ROOT_ATTRIBUTE,
    DEFAULT_APPEARANCE,
    APPEARANCE_TOGGLE_ENABLED,
    isAppearance,
    readStoredAppearance,
    writeStoredAppearance,
    resolveInitialAppearance,
    applyAppearance,
    controlState,
    bindAppearanceController,
  };

  if (windowRef.document) bindAppearanceController();
})(window);
